import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { CheckOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import flagDe from '@assets/images/flags/de.svg';
import flagEn from '@assets/images/flags/en.svg';
import flagVi from '@assets/images/flags/vi.svg';
import InputTextField from '@components/common/form/InputTextField';
import { appAccount, companyName, grantType, UserTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useFetchAction from '@hooks/useFetchAction';
import useLocale from '@hooks/useLocale';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showErrorMessage } from '@services/notifyService';
import { removeCacheX_tenant, setCacheAccessToken, setCacheIsAdmin } from '@services/userService';
import { accountActions, appActions, restaurantActions } from '@store/actions';
import { Button, Dropdown, Form, Tooltip } from 'antd';
import Title from 'antd/es/typography/Title';
import { Buffer } from 'buffer';

import styles from './index.module.scss';
window.Buffer = window.Buffer || Buffer;
const message = defineMessages({
    copyRight: '© {year} {companyName}. All rights reserved.',
    loginFail: 'Wrong username or password!',
    verifyFailOTP: 'OTP code is incorrect!',
    verifySuccessOTP: 'OTP verification successful!',
    loginNoAccess: 'Account type not suitable!',
    username: 'Username',
    otp: 'OTP',
    password: 'Password',
    cancel: 'Cancel',
    language: {
        id: 'header.language',
        defaultMessage: 'Language',
    },
    usernameInvalid: 'Username is invalid!',
    passwordInvalid: 'Password is invalid!',
});

const languageOptions = [
    { value: 'en', label: 'English', flag: flagEn },
    { value: 'de', label: 'Deutsch', flag: flagDe },
    { value: 'vi', label: 'Tiếng Việt', flag: flagVi },
];

const LoginPage = () => {
    const [ form ] = Form.useForm();
    const intl = useIntl();
    const dispatch = useDispatch();
    const { locale } = useLocale();
    const translate = useTranslate();
    const selectedLanguage = languageOptions.find((item) => item.value === locale) || languageOptions[0];
    const base64Credentials = Buffer.from(`${appAccount.APP_USERNAME}:${appAccount.APP_PASSWORD}`).toString('base64');
    const { execute, loading } = useFetch({
        ...apiConfig.account.loginBasic,
        authorization: `Basic ${base64Credentials}`,
    });

    const { execute: executeGetProfile } = useFetchAction(accountActions.getProfile, {
        loading: useFetchAction.LOADING_TYPE.APP,
    });
    const handleChangeLocale = (nextLocale) => {
        dispatch(appActions.changeLanguage(nextLocale));
    };

    const onFinish = (values) => {
        execute({
            data: {
                ...values,
                grant_type: grantType,
            },
            onCompleted: (res) => {
                handleLoginSuccess(res);
            },
            onError: () => showErrorMessage(translate.formatMessage(message.loginFail), translate),
        });
    };

    const handleLoginSuccess = (res) => {
        const isAdmin = res.user_kind === UserTypes.ADMIN;
        setCacheIsAdmin(isAdmin);
        setCacheAccessToken(res.access_token);
        if (!isAdmin) {
            removeCacheX_tenant();
            dispatch(restaurantActions.clearRestaurant());
        }
        executeGetProfile();
    };

    const checkUserName = (_, value) => {
        if (value) {
            const usernameRegex = /^[a-zA-Z0-9_]{2,20}$/;
            if (!usernameRegex.test(value)) {
                return Promise.reject(intl.formatMessage(message.usernameInvalid));
            }
        } else return Promise.reject(intl.formatMessage(message.usernameInvalid));

        return Promise.resolve();
    };
    const checkPassword = (_, value) => {
        if (value) {
            const passwordRegex = /^[A-Za-z\d!@#$%^&*()_+\-=]{6,}$/;
            if (!passwordRegex.test(value)) {
                return Promise.reject(intl.formatMessage(message.passwordInvalid));
            }
        } else return Promise.reject(intl.formatMessage(message.passwordInvalid));

        return Promise.resolve();
    };

    return (
        <div className={styles.loginPage}>
            <div className={styles.loginForm}>
                <div className={styles.languageSelector}>
                    <Dropdown
                        trigger={[ 'click' ]}
                        placement="bottomRight"
                        menu={{
                            selectedKeys: [ locale ],
                            items: languageOptions.map((item) => ({
                                key: item.value,
                                label: item.label,
                                icon: (
                                    <span className={styles.languageOptionIcon}>
                                        <img src={item.flag} alt="" />
                                        {locale === item.value ? (
                                            <CheckOutlined />
                                        ) : (
                                            <span className={styles.checkSpacer} />
                                        )}
                                    </span>
                                ),
                                onClick: () => handleChangeLocale(item.value),
                            })),
                        }}
                    >
                        <Tooltip title={translate.formatMessage(message.language)}>
                            <Button
                                className={styles.languageButton}
                                type="text"
                                icon={<img src={selectedLanguage.flag} alt={selectedLanguage.label} />}
                                aria-label={translate.formatMessage(message.language)}
                            />
                        </Tooltip>
                    </Dropdown>
                </div>
                <Title level={3}>{intl.formatMessage(commonMessage.login).toUpperCase()}</Title>
                <Form form={form} name="login-form" onFinish={onFinish} layout="vertical">
                    <InputTextField
                        name="username"
                        fieldProps={{ prefix: <UserOutlined /> }}
                        label={intl.formatMessage(message.username)}
                        placeholder={intl.formatMessage(commonMessage.username)}
                        size="large"
                        rules={[
                            {
                                required: true,
                                validator: checkUserName,
                            },
                        ]}
                    />
                    <InputTextField
                        name="password"
                        fieldProps={{ prefix: <LockOutlined /> }}
                        label={intl.formatMessage(message.password)}
                        placeholder={intl.formatMessage(commonMessage.password)}
                        size="large"
                        type="password"
                        rules={[
                            {
                                required: true,
                                validator: checkPassword,
                            },
                        ]}
                    />
                    <Button type="primary" size="large" loading={loading} htmlType="submit" style={{ width: '100%' }}>
                        {intl.formatMessage(commonMessage.login)}
                    </Button>
                    <center className="s-mt4px">
                        <small>
                            {intl.formatMessage(message.copyRight, { companyName, year: new Date().getFullYear() })}
                        </small>
                    </center>
                </Form>
            </div>
        </div>
    );
};

export default LoginPage;
